import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Sparkles, ArrowRight, ArrowLeft, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// Non-closable modal shown right after activation/first login.
// 5 steps, one question per step, no "skip" — the user must complete.
export default function OnboardingModal({ open }) {
  const { t } = useTranslation();
  const { refreshUser } = useAuth();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    job_title: "",
    industry: "",
    usage_type: "",
    team_size: "",
    heard_from: "",
  });

  const usageOptions = [
    { value: "perso", label: t("onboarding.usage.perso") },
    { value: "pro", label: t("onboarding.usage.pro") },
    { value: "freelance", label: t("onboarding.usage.freelance") },
    { value: "equipe", label: t("onboarding.usage.equipe") },
    { value: "education", label: t("onboarding.usage.education") },
  ];

  const teamSizeOptions = [
    { value: "solo", label: t("onboarding.teamSize.solo") },
    { value: "2-5", label: "2 – 5" },
    { value: "6-20", label: "6 – 20" },
    { value: "20+", label: "20+" },
  ];

  const heardOptions = [
    { value: "google", label: t("onboarding.heard.google") },
    { value: "social", label: t("onboarding.heard.social") },
    { value: "friend", label: t("onboarding.heard.friend") },
    { value: "ad", label: t("onboarding.heard.ad") },
    { value: "other", label: t("onboarding.heard.other") },
  ];

  const steps = [
    {
      key: "job_title",
      title: t("onboarding.steps.job.title"),
      subtitle: t("onboarding.steps.job.subtitle"),
      type: "text",
      placeholder: t("onboarding.steps.job.placeholder"),
    },
    {
      key: "industry",
      title: t("onboarding.steps.industry.title"),
      subtitle: t("onboarding.steps.industry.subtitle"),
      type: "text",
      placeholder: t("onboarding.steps.industry.placeholder"),
    },
    {
      key: "usage_type",
      title: t("onboarding.steps.usage.title"),
      subtitle: t("onboarding.steps.usage.subtitle"),
      type: "options",
      options: usageOptions,
    },
    {
      key: "team_size",
      title: t("onboarding.steps.teamSize.title"),
      subtitle: t("onboarding.steps.teamSize.subtitle"),
      type: "options",
      options: teamSizeOptions,
    },
    {
      key: "heard_from",
      title: t("onboarding.steps.heard.title"),
      subtitle: t("onboarding.steps.heard.subtitle"),
      type: "options",
      options: heardOptions,
    },
  ];

  const current = steps[step];
  const value = form[current.key];
  const canContinue = Boolean(value && value.trim());
  const isLast = step === steps.length - 1;
  const progress = ((step + 1) / steps.length) * 100;

  const handleNext = async () => {
    if (!canContinue) return;
    if (!isLast) {
      setStep((s) => s + 1);
      return;
    }
    // Submit final
    setSaving(true);
    try {
      await api.post("/auth/onboarding", form);
      await refreshUser();
      toast.success(t("onboarding.success"));
    } catch (e) {
      toast.error(e.response?.data?.detail || t("common.error"));
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    if (step > 0) setStep((s) => s - 1);
  };

  const onEnter = (e) => {
    if (e.key === "Enter" && canContinue && !saving) {
      e.preventDefault();
      handleNext();
    }
  };

  return (
    <Dialog open={open} dismissible={false}>
      <DialogContent
        data-testid="onboarding-modal"
        className="sm:max-w-lg rounded-sm border-stone-200 [&>button]:hidden"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader className="space-y-3">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#E05236] font-semibold">
            <Sparkles className="w-3.5 h-3.5" />
            {t("onboarding.header")}
          </div>
          <DialogTitle className="font-display text-2xl sm:text-3xl font-black tracking-tight">
            {current.title}
          </DialogTitle>
          <DialogDescription className="text-sm text-neutral-600">
            {current.subtitle}
          </DialogDescription>
        </DialogHeader>

        {/* Progress */}
        <div className="mt-2">
          <div className="h-0.5 bg-stone-200 rounded-sm overflow-hidden">
            <div
              data-testid="onboarding-progress"
              className="h-full bg-[#E05236] transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="mt-2 text-[10px] uppercase tracking-[0.2em] text-neutral-500">
            {t("onboarding.stepOf", { current: step + 1, total: steps.length })}
          </div>
        </div>

        {/* Body */}
        <div className="py-3">
          {current.type === "text" ? (
            <div className="space-y-2">
              <Label htmlFor="ob-input" className="text-xs uppercase tracking-[0.15em] text-neutral-600">
                {t("onboarding.yourAnswer")}
              </Label>
              <Input
                id="ob-input"
                data-testid={`onboarding-input-${current.key}`}
                autoFocus
                value={value}
                onChange={(e) => setForm({ ...form, [current.key]: e.target.value })}
                onKeyDown={onEnter}
                placeholder={current.placeholder}
                className="h-11 rounded-sm border-stone-300"
              />
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-2">
              {current.options.map((opt) => {
                const selected = value === opt.value;
                return (
                  <button
                    type="button"
                    key={opt.value}
                    data-testid={`onboarding-option-${current.key}-${opt.value}`}
                    onClick={() => setForm({ ...form, [current.key]: opt.value })}
                    className={`text-left border rounded-sm px-4 py-3 text-sm transition-colors flex items-center justify-between ${
                      selected
                        ? "border-[#E05236] bg-[#E05236]/5 text-neutral-950 font-medium"
                        : "border-stone-200 hover:border-neutral-400 text-neutral-800"
                    }`}
                  >
                    <span>{opt.label}</span>
                    {selected && <CheckCircle2 className="w-4 h-4 text-[#E05236]" />}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className="flex items-center justify-between pt-2">
          <Button
            variant="ghost"
            onClick={handleBack}
            disabled={step === 0 || saving}
            data-testid="onboarding-back-btn"
            className="rounded-sm text-xs uppercase tracking-[0.15em] disabled:opacity-30"
          >
            <ArrowLeft className="w-3.5 h-3.5 mr-1.5" />
            {t("common.back")}
          </Button>

          <Button
            onClick={handleNext}
            disabled={!canContinue || saving}
            data-testid="onboarding-next-btn"
            className="rounded-sm bg-neutral-950 hover:bg-neutral-800 text-white h-11 px-5 disabled:opacity-40"
          >
            {saving ? "..." : isLast ? t("onboarding.finish") : t("onboarding.next")}
            {!saving && <ArrowRight className="w-3.5 h-3.5 ml-1.5" />}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
